import { getDefaultWomanImageUrl, getNewsLargeImageUrl, getWomanLargeImageUrl } from "./imageUrl";
import { DEFAULT_LANGUAGE, LANGUAGES, translations } from "../i18n/translations";
import { getLanguageFromPath, localizedPath, stripLanguageFromPath } from "../i18n/LanguageProvider";
import { BRAND_FULL_NAME } from "../config/brand";
import { getIndexableLanguages, getInitialPath, getProfileIndexMode } from "./initialData";
import { plural } from "./plural";

export const DEFAULT_OG_IMAGE = getDefaultWomanImageUrl();
export const SITE_NAME = BRAND_FULL_NAME;

const getTranslation = (language = DEFAULT_LANGUAGE) => translations[language] || translations[DEFAULT_LANGUAGE];

export const DEFAULT_SEO = {
    title: getTranslation().seo.defaultTitle,
    description: getTranslation().seo.defaultDescription,
    image: DEFAULT_OG_IMAGE,
    type: "website",
};

function buildStaticSeo(language = DEFAULT_LANGUAGE) {
    const dictionary = getTranslation(language);
    const siteName = BRAND_FULL_NAME;
    const defaultSeo = {
        title: dictionary.seo.defaultTitle,
        description: dictionary.seo.defaultDescription,
        image: DEFAULT_OG_IMAGE,
        type: "website",
    };

    return {
        "/": defaultSeo,
        "/despre-noi": {
            title: `${dictionary.about.title} | ${siteName}`,
            description: dictionary.about.sections?.[0]?.paragraphs?.[0] || dictionary.seo.defaultDescription,
            image: DEFAULT_OG_IMAGE,
            type: "website",
        },
        "/contact": {
            title: `${dictionary.contact.title} | ${siteName}`,
            description: dictionary.contact.intro,
            image: DEFAULT_OG_IMAGE,
            type: "website",
        },
        "/noutati": {
            title: `${dictionary.news.title} | ${siteName}`,
            description: dictionary.news.seoDescription,
            image: DEFAULT_OG_IMAGE,
            type: "website",
        },
        "/femei": {
            title: `${dictionary.directory.title} | ${siteName}`,
            description: dictionary.directory.seoDescription,
            image: DEFAULT_OG_IMAGE,
            type: "website",
        },
        "/politica-de-confidentialitate": {
            title: `${dictionary.legal.privacyTitle} | ${siteName}`,
            description: dictionary.legal.privacySections?.[0]?.paragraphs?.[0] || dictionary.seo.defaultDescription,
            image: DEFAULT_OG_IMAGE,
            type: "article",
        },
        "/termeni-si-conditii": {
            title: `${dictionary.legal.termsTitle} | ${siteName}`,
            description: dictionary.legal.termsSections?.[1]?.paragraphs?.[0] || dictionary.seo.defaultDescription,
            image: DEFAULT_OG_IMAGE,
            type: "article",
        },
        "/admin": {
            title: `Administrare | ${siteName}`,
            description: "Panou administrativ cu acces restricționat.",
            image: DEFAULT_OG_IMAGE,
            type: "website",
            robots: "noindex, nofollow",
        },
    };
}

const cleanText = (text = "") =>
    String(text)
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const trimPunctuation = (text) => text.replace(/[\s,;:.-]+$/g, "");

export function limitText(text, maxLength = 158) {
    const clean = cleanText(text);

    if (clean.length <= maxLength) {
        return clean;
    }

    const sliced = clean.slice(0, maxLength - 1);
    const lastSpace = sliced.lastIndexOf(" ");
    const end = lastSpace > 90 ? lastSpace : maxLength - 1;

    return `${trimPunctuation(sliced.slice(0, end))}…`;
}

export function absoluteUrl(path) {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;

    return `${origin}${normalizedPath}`;
}

function upsertMeta(attributeName, attributeValue, content) {
    if (!content || typeof document === "undefined") return;

    const selector = `meta[${attributeName}="${attributeValue}"]`;
    let element = document.head.querySelector(selector);

    if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
    }

    element.setAttribute("content", content);
}

function upsertCanonical(url) {
    if (!url || typeof document === "undefined") return;

    let element = document.head.querySelector('link[rel="canonical"]');

    if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", "canonical");
        document.head.appendChild(element);
    }

    element.setAttribute("href", url);
}

function removeAlternateLinks() {
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((element) => element.remove());
}

// hreflang is only announced between versions that may be indexed.
function upsertAlternateLinks(currentPathname, search, isIndexable) {
    if (typeof document === "undefined") return;

    removeAlternateLinks();

    const indexableLanguages = getIndexableLanguages();

    if (!isIndexable || indexableLanguages.length < 2) return;

    const addLink = (hreflang, languageCode) => {
        const element = document.createElement("link");
        element.setAttribute("rel", "alternate");
        element.setAttribute("hreflang", hreflang);
        element.setAttribute(
            "href",
            absoluteUrl(localizedPath(stripLanguageFromPath(currentPathname), languageCode)) + search,
        );
        element.setAttribute("data-i18n", "true");
        document.head.appendChild(element);
    };

    LANGUAGES.filter((item) => indexableLanguages.includes(item.code)).forEach((item) =>
        addLink(item.htmlLang, item.code),
    );

    if (indexableLanguages.includes(DEFAULT_LANGUAGE)) {
        addLink("x-default", DEFAULT_LANGUAGE);
    }
}

// One canonical per page: no trailing slash, and only the "page" parameter survives.
function getCanonicalParts() {
    const pathname = window.location.pathname.replace(/\/+$/, "") || "/";
    const page = parseInt(new URLSearchParams(window.location.search).get("page"), 10);
    const search = page > 1 ? `?page=${page}` : "";

    return { pathname, search, url: `${window.location.origin}${pathname}${search}` };
}

export function resolveRobots(seo, language) {
    const robots = seo.robots || "index, follow";

    if (!/noindex/i.test(robots) && !getIndexableLanguages().includes(language)) {
        return "noindex, follow";
    }

    return robots;
}

export function applySeo(seo = DEFAULT_SEO) {
    if (typeof document === "undefined") return;

    const canonical = getCanonicalParts();
    const title = seo.title || DEFAULT_SEO.title;
    const description = limitText(seo.description || DEFAULT_SEO.description);
    const image = absoluteUrl(seo.image || DEFAULT_OG_IMAGE);
    const url = seo.url || canonical.url;
    const type = seo.type || "website";
    const language = seo.language || getLanguageFromPath(window.location.pathname);
    const locale = LANGUAGES.find((item) => item.code === language)?.ogLocale || "ro_MD";
    const robots = resolveRobots(seo, language);

    document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", BRAND_FULL_NAME);
    upsertMeta("property", "og:locale", locale);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);
    upsertCanonical(url);
    upsertAlternateLinks(canonical.pathname, canonical.search, !/noindex/i.test(robots));

    // The structured data printed by the server describes the page it rendered; after a
    // client-side navigation it would describe the wrong page, so it is removed.
    if (canonical.pathname !== getInitialPath()) {
        document.getElementById("schema-main")?.remove();
    }
}

export function getStaticSeoForPath(pathname, language = DEFAULT_LANGUAGE) {
    const dictionary = getTranslation(language);
    const cleanPath = stripLanguageFromPath(pathname);

    if (cleanPath?.startsWith("/femeie/")) {
        return {
            title: `${dictionary.seo.profileFallbackTitle} | ${BRAND_FULL_NAME}`,
            description: dictionary.seo.profileFallbackDescription,
            image: DEFAULT_OG_IMAGE,
            type: "profile",
            language,
        };
    }

    const staticSeo = buildStaticSeo(language);

    if (cleanPath?.startsWith("/categorie/") || cleanPath?.startsWith("/tara/")) {
        return { ...staticSeo["/femei"], language };
    }

    if (!staticSeo[cleanPath] && !cleanPath?.startsWith("/noutati/")) {
        return buildNotFoundSeo(language);
    }

    return { ...(staticSeo[cleanPath] || staticSeo["/noutati"]), language };
}

export function buildNotFoundSeo(language = DEFAULT_LANGUAGE) {
    const dictionary = getTranslation(language);

    return {
        title: `${dictionary.notFound.title} | ${BRAND_FULL_NAME}`,
        description: dictionary.notFound.text,
        image: DEFAULT_OG_IMAGE,
        type: "website",
        robots: "noindex, follow",
        language,
    };
}

const fillTemplate = (template, params) =>
    Object.entries(params).reduce((text, [key, value]) => text.replaceAll(`{{${key}}}`, String(value)), template || "");

/** Texts of a category / country page (mirror of serverRenderTaxonomyCopy() in backend/serverRender.php). */
export function buildTaxonomyCopy(taxonomyPage, language = DEFAULT_LANGUAGE) {
    const directory = getTranslation(language).directory;
    const isCategory = taxonomyPage.type === "category";
    const params = {
        name: taxonomyPage.item.name,
        total: plural(directory.profiles, taxonomyPage.item.total, language),
    };

    return {
        title: fillTemplate(isCategory ? directory.categoryTitle : directory.placeTitle, params),
        heading: fillTemplate(isCategory ? directory.categoryHeading : directory.placeHeading, params),
        intro: fillTemplate(isCategory ? directory.categoryIntro : directory.placeIntro, params),
        breakdownLabel: isCategory ? directory.categoryBreakdown : directory.placeBreakdown,
        description: fillTemplate(
            isCategory ? directory.categorySeoDescription : directory.placeSeoDescription,
            params,
        ),
        pageInfo: fillTemplate(directory.pageInfo, { page: taxonomyPage.page, totalPages: taxonomyPage.totalPages }),
    };
}

export function buildTaxonomySeo(taxonomyPage, language = DEFAULT_LANGUAGE) {
    const copy = buildTaxonomyCopy(taxonomyPage, language);
    const pageSuffix = taxonomyPage.page > 1 ? ` — ${copy.pageInfo}` : "";

    return {
        title: limitText(`${copy.title}${pageSuffix}`, 110),
        description: copy.description,
        image: DEFAULT_OG_IMAGE,
        type: "website",
        ...(taxonomyPage.page > 1 ? { robots: "noindex, follow" } : {}),
        language,
    };
}

export function buildDirectoryIntro(directory, language = DEFAULT_LANGUAGE) {
    const copy = getTranslation(language).directory;

    return fillTemplate(copy.intro, {
        total: plural(copy.profiles, directory?.totalWomen || 0, language),
        categories: plural(copy.categoriesCount, directory?.categories?.length || 0, language),
        places: plural(copy.placesCount, directory?.places?.length || 0, language),
    });
}

export function buildNewsSeo(news, language = DEFAULT_LANGUAGE) {
    const dictionary = getTranslation(language);
    const title = cleanText(news?.title) || dictionary.news.title;
    const content = cleanText(news?.content);
    const description = content || cleanText(news?.excerpt) || dictionary.news.seoDescription;

    return {
        title: limitText(`${title} | ${BRAND_FULL_NAME}`, 95),
        description: limitText(description),
        image: getNewsLargeImageUrl(news?.image_url),
        type: "article",
        language,
    };
}

export function buildWomanSeo(woman, language = DEFAULT_LANGUAGE) {
    const dictionary = getTranslation(language);
    const fullName = cleanText(`${woman?.name || ""} ${woman?.surname || ""}`) || "Profil";
    const achievement = cleanText(woman?.achievement);
    const description = cleanText(woman?.description);
    const category = cleanText(woman?.category_name);
    const titleDetail = achievement || category || "poveste de succes";
    const story =
        achievement ||
        description ||
        dictionary.seo.womanIncluded.replace("{{name}}", fullName);
    const image = woman?.image ? getWomanLargeImageUrl(woman.image) : DEFAULT_OG_IMAGE;
    const seoDescription = `${fullName}: ${story}. ${dictionary.seo.discoverStory.replace(
        "{{siteName}}",
        BRAND_FULL_NAME,
    )}`;
    const expandedDescription =
        seoDescription.length < 145
            ? `${seoDescription} ${dictionary.seo.publicInfo}`
            : seoDescription;

    const hasSources = /https?:\/\//i.test(String(woman?.sources || ""));

    return {
        title: limitText(`${fullName} — ${titleDetail} | ${BRAND_FULL_NAME}`, 95),
        description: limitText(expandedDescription),
        image,
        type: "profile",
        ...(getProfileIndexMode() === "with_sources" && !hasSources ? { robots: "noindex, follow" } : {}),
        language,
    };
}
